<script setup lang="ts">
import { computed } from 'vue';
import { useStore } from 'vuex';

import WindowObserver from '@/components/base/WindowObserver.vue';
import LineItems from '@/components/checkout/summary/LineItems.vue';
import LineItemsCollapse from '@/components/checkout/summary/LineItemsCollapse.vue';
import PaymentItems from '@/components/checkout/summary/PaymentItems.vue';
import { useCart } from '@/composables/useCart';
import { NutsLineItem } from '@/lib/cart/lineItem';
import { isPresetDeliveryLineItem, Shipment } from '@/utils/cart';

const props = defineProps<{
  containsAutoDelivery?: boolean;
  lineItems: NutsLineItem[];
  shipments: Shipment[];
}>();

const collapsed = defineModel<boolean>();

const store = useStore();
const { unassignedLineItems } = useCart(store);

const specialDeliveryLineItems = computed(() => props.lineItems.filter(isPresetDeliveryLineItem));

const handleExit = () => {
  collapsed.value = true;
};
</script>

<template>
  <div class="order-summary md:sticky md:top-6" data-test="order-summary-section">
    <div
      class="transition-all duration-500 ease-in-out"
      :class="{ 'hidden md:block md:visible': collapsed }"
    >
      <PaymentItems />
      <WindowObserver @exit-view="handleExit">
        <LineItemsCollapse :lineItems="lineItems">
          <LineItems
            :containsAutoDelivery="containsAutoDelivery"
            :lineItems="lineItems"
            :shipments="shipments"
            :specialDeliveryLineItems="specialDeliveryLineItems"
            :unassignedLineItems="unassignedLineItems"
            class="pr-4 md:mt-6 lg:mt-6 md:max-h-96 md:styled-scrollbar"
          />
          <template #footer>
            <small
              v-if="containsAutoDelivery"
              class="ml-4 text-xs font-proxima-nova"
              data-test="auto-delivery-note"
            >
              Note: this order contains Auto-Delivery
            </small>
          </template>
        </LineItemsCollapse>
      </WindowObserver>
      <div v-once id="acadia-usps-row" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.shadow-b {
  box-shadow: 0px 2px 5px -1px rgb(161 161 161 / 50%);
}
.styled-scrollbar {
  &::-webkit-scrollbar {
    width: 6px;
    height: auto;
    background-color: $alto;
    border-radius: 9999px;
    scrollbar-color: $dark-grey $alto;

    &-thumb {
      background: $dark-grey;
      border-radius: 9999px;
    }
  }
}
</style>
